/** Tableaux croisés dynamiques : agrégation d'une plage de cellules. */

export type PivotAgg = 'sum' | 'count' | 'avg' | 'min' | 'max';

export interface PivotConfig {
  r1: number;
  c1: number;
  r2: number;
  c2: number;
  /** Index de colonne (relatif à c1) utilisé en lignes. */
  rowField: number;
  /** Index de colonne utilisé en colonnes (optionnel : -1 = aucun). */
  colField: number;
  /** Index de colonne des valeurs. */
  valueField: number;
  agg: PivotAgg;
}

export interface PivotResult {
  headers: string[];
  rows: string[][];
  totals: string[];
}

export const PIVOT_AGG_LABELS: Record<PivotAgg, string> = {
  sum: 'Somme',
  count: 'Nombre',
  avg: 'Moyenne',
  min: 'Minimum',
  max: 'Maximum',
};

function toNumber(value: string): number {
  const cleaned = (value || '').replace(/\s/g, '').replace(',', '.').replace(/[^0-9.-]/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : NaN;
}

export function buildPivot(
  cells: Record<string, string>,
  config: PivotConfig,
): PivotResult {
  const { r1, c1, r2, c2, rowField, colField, valueField, agg } = config;
  const get = (r: number, c: number) => cells[`${r},${c}`] || '';

  // La première ligne de la plage sert d'en-tête.
  const headerRow = r1;
  const dataStart = r1 + 1;
  const rowHeader = get(headerRow, c1 + rowField) || 'Ligne';
  const colHeader = colField >= 0 ? get(headerRow, c1 + colField) || 'Colonne' : '';
  const valueHeader = get(headerRow, c1 + valueField) || 'Valeur';

  const rowKeys: string[] = [];
  const colKeys: string[] = [];
  const buckets = new Map<string, number[]>();

  for (let r = dataStart; r <= r2; r += 1) {
    const rowKey = get(r, c1 + rowField).trim();
    if (!rowKey) continue;
    const colKey = colField >= 0 ? get(r, c1 + colField).trim() : '';
    const rawValue = get(r, c1 + valueField);
    const numeric = toNumber(rawValue);
    if (!rowKeys.includes(rowKey)) rowKeys.push(rowKey);
    if (colField >= 0 && !colKeys.includes(colKey)) colKeys.push(colKey);
    const key = `${rowKey}\u0000${colKey}`;
    const list = buckets.get(key) || [];
    list.push(agg === 'count' ? 1 : Number.isFinite(numeric) ? numeric : 0);
    buckets.set(key, list);
  }

  const aggregate = (values: number[]): number => {
    if (!values.length) return 0;
    switch (agg) {
      case 'count':
        return values.length;
      case 'avg':
        return values.reduce((a, b) => a + b, 0) / values.length;
      case 'min':
        return Math.min(...values);
      case 'max':
        return Math.max(...values);
      default:
        return values.reduce((a, b) => a + b, 0);
    }
  };

  const format = (n: number): string => {
    if (!Number.isFinite(n)) return '0';
    const rounded = Math.round(n * 100) / 100;
    return String(rounded).replace('.', ',');
  };

  if (colField < 0) {
    const headers = [rowHeader, `${PIVOT_AGG_LABELS[agg]} de ${valueHeader}`];
    const rows = rowKeys.map((rk) => [rk, format(aggregate(buckets.get(`${rk}\u0000`) || []))]);
    const total = rowKeys.reduce((acc, rk) => acc + aggregate(buckets.get(`${rk}\u0000`) || []), 0);
    return { headers, rows, totals: [agg === 'count' ? 'Total' : 'Total', format(agg === 'avg' ? (rows.length ? total / rows.length : 0) : total)] };
  }

  const headers = [rowHeader, ...colKeys, 'Total'];
  const rows = rowKeys.map((rk) => {
    const values = colKeys.map((ck) => aggregate(buckets.get(`${rk}\u0000${ck}`) || []));
    const rowTotal = agg === 'avg' ? values.reduce((a, b) => a + b, 0) / (values.length || 1) : values.reduce((a, b) => a + b, 0);
    return [rk, ...values.map(format), format(rowTotal)];
  });
  const totals = [
    'Total',
    ...colKeys.map((ck) => {
      const values = rowKeys.map((rk) => aggregate(buckets.get(`${rk}\u0000${ck}`) || []));
      return format(agg === 'avg' ? values.reduce((a, b) => a + b, 0) / (values.length || 1) : values.reduce((a, b) => a + b, 0));
    }),
    format(0),
  ];
  return { headers, rows, totals };
}